import appConfig from "@/shared/config/app";
import {
  Autocomplete,
  AutocompleteItem
} from "@nextui-org/react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import VendorLogo from "./VendorLogo";

interface Suggestion {
  name: string;
  domain: string;
}

interface VendorSearchProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onSelectionChange?: (value: any) => void;
  onInputChange?: (value: string) => void;
  errorMessage?: string;
  isInvalid?: boolean;
}

const VendorSearch = ({
  label = "Search vendor",
  placeholder = "Type a company name...",
  value = "",
  onSelectionChange,
  onInputChange,
  errorMessage,
  isInvalid = false
}: VendorSearchProps) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync internal state when value prop changes (e.g., when navigating back)
  useEffect(() => {
    setQuery(value);
  }, [value]);

  const handleSearch = (val: string) => {
    setQuery(val);
    onInputChange?.(val);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      if (val.length > 1) {
        try {
          const resp = await axios.get("https://api.logo.dev/search", {
            params: { q: val },
            headers: {
              Authorization: `Bearer ${appConfig.logoDevAuthKey}`,
            },
          });
          setSuggestions(resp.data);
        } catch (err) {
          console.error("Search error", err);
        }
      } else {
        setSuggestions([]);
      }
    }, 300);
  };

  const handleSelectionChange = (key: any) => {
    if (key) {
      const selectedSuggestion = suggestions.find(s => s.domain === key);
      if (selectedSuggestion) {
        setQuery(selectedSuggestion.name);
        const vendorObject = {
          name: selectedSuggestion.name,
          domain: selectedSuggestion.domain,
          logo_url: `https://img.logo.dev/${selectedSuggestion.domain}`
        };
        onSelectionChange?.(vendorObject);
      }
    }
  };

  return (
    <div className="w-full">
      <Autocomplete
        label={label}
        placeholder={placeholder}
        inputValue={query}
        onInputChange={handleSearch}
        onSelectionChange={handleSelectionChange}
        allowsCustomValue={true}
        labelPlacement="outside"
        errorMessage={errorMessage}
        isInvalid={isInvalid}
        variant="faded"
        className="w-full [&_[data-slot=input-wrapper]]:rounded-md [&_[data-slot=input-wrapper]]:border-1 [&_[data-slot=input-wrapper]]:border-gray-300 [&_[data-slot=input-wrapper]]:shadow-none [&_[data-slot=input-wrapper]]:data-[hover=true]:border-gray-300 [&_[data-slot=input-wrapper]]:focus-within:border-gray-400"
        classNames={{
          selectorButton: "hidden",
        }}
        disableSelectorIconRotation
        selectorIcon={false}
      >
        {suggestions.map((s) => (
          <AutocompleteItem
            className="p-3"
            key={s.domain}
            textValue={s.name}
            startContent={
              <VendorLogo
                alt={s.name}
                src={`https://img.logo.dev/${s.domain}`}
                needsAuthToken
                size={32}
                className="rounded-full"
                radius="sm"
              />
            }
          >
            <div className="flex flex-col">
              <span className="text-small">{s.name}</span>
              <span className="text-tiny text-default-400">{s.domain}</span>
            </div>
          </AutocompleteItem>
        ))}
      </Autocomplete>
    </div>
  );
};

export default VendorSearch;
